import { useState, type FormEvent } from "react";
import {
  Banner,
  Button,
  FieldSelect,
  FieldText,
  LoadingAnimation,
  useToast,
} from "@knowtex-ai/components";
import { MC_MAX_BROWSERS, type McSettings } from "../../lib/mc";
import { useMcSettingsQuery, useUpdateMcSettings } from "../../hooks/useMcRuns";
import McHeader from "./McHeader";
import McPageShell from "./McPageShell";
import { formatDateTime } from "./format";

interface McSettingsPageProps {
  setAuth: (isAuthenticated: boolean) => void;
  setUserOrg: (org: string | null) => void;
}

const MAX_BUDGET_USD = 20;
const MAX_MINUTES = 120;

const BROWSER_OPTIONS = Array.from({ length: MC_MAX_BROWSERS }, (_, i) => ({
  value: String(i + 1),
  label: String(i + 1),
}));

/** A positive number up to `max` (whole when `integer`), or undefined. */
const parse = (text: string, max: number, integer = false) => {
  const value = Number(text.trim());
  if (!text.trim() || !Number.isFinite(value) || value <= 0 || value > max)
    return undefined;
  if (integer && !Number.isInteger(value)) return undefined;
  return value;
};

function SettingsForm({ settings }: { settings: McSettings }) {
  const toast = useToast();
  const update = useUpdateMcSettings();
  const [budget, setBudget] = useState(String(settings.budgetUsd));
  const [minutes, setMinutes] = useState(String(settings.maxMinutes));
  const [browsers, setBrowsers] = useState(String(settings.maxBrowsers));
  const budgetUsd = parse(budget, MAX_BUDGET_USD);
  const maxMinutes = parse(minutes, MAX_MINUTES, true);
  const changed =
    budgetUsd !== settings.budgetUsd ||
    maxMinutes !== settings.maxMinutes ||
    Number(browsers) !== settings.maxBrowsers;

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (budgetUsd === undefined || maxMinutes === undefined) return;
    update.mutate(
      { budgetUsd, maxMinutes, maxBrowsers: Number(browsers) },
      {
        onSuccess: () => toast.success("Settings saved"),
        onError: (error) =>
          toast.error("Couldn't save the settings", error.message),
      },
    );
  };

  return (
    <form onSubmit={submit} className="flex flex-col gap-5">
      <div className="flex max-w-[240px] flex-col gap-5">
        <FieldText
          label="Max cost per run"
          description={`Up to $${MAX_BUDGET_USD}`}
          prefix="$"
          value={budget}
          onChange={(e) => setBudget(e.target.value)}
          error={
            budget.trim() && budgetUsd === undefined
              ? `Enter an amount up to $${MAX_BUDGET_USD}`
              : undefined
          }
          autoComplete="off"
          required
          disabled={update.isPending}
        />
        <FieldText
          label="Max time per run"
          description={`Up to ${MAX_MINUTES / 60} hours`}
          suffix="minutes"
          value={minutes}
          onChange={(e) => setMinutes(e.target.value)}
          error={
            minutes.trim() && maxMinutes === undefined
              ? `Enter whole minutes, up to ${MAX_MINUTES}`
              : undefined
          }
          autoComplete="off"
          required
          disabled={update.isPending}
        />
        <FieldSelect
          label="Max concurrent browsers"
          description="Individual missions are split between browsers when possible"
          items={BROWSER_OPTIONS}
          value={browsers}
          onChange={(value) => value && setBrowsers(value)}
          disabled={update.isPending}
        />
      </div>
      <div className="flex flex-col items-start gap-4 pt-2">
        <Button
          type="submit"
          loading={update.isPending}
          disabled={
            !changed || budgetUsd === undefined || maxMinutes === undefined
          }
        >
          Save
        </Button>
        {settings.updatedAt && (
          <span className="text-[13px] text-gray-500">
            {settings.updatedBy
              ? `Updated by ${settings.updatedBy} on ${formatDateTime(settings.updatedAt)}`
              : `Updated on ${formatDateTime(settings.updatedAt)}`}
          </span>
        )}
      </div>
    </form>
  );
}

function McSettingsView() {
  const query = useMcSettingsQuery();
  const body = () => {
    if (query.isLoading) {
      return (
        <div className="flex justify-center py-24 text-gray-400">
          <LoadingAnimation size={28} />
        </div>
      );
    }
    if (query.isError || !query.data) {
      return (
        <Banner variant="error" heading="Couldn't load the settings">
          {query.error?.message}
        </Banner>
      );
    }
    return (
      <SettingsForm
        key={query.data.updatedAt ?? "defaults"}
        settings={query.data}
      />
    );
  };
  return (
    <>
      <McHeader section="settings" />
      <div className="mt-8">
        <Banner variant="brand" heading="How settings work">
          These apply to every run on this target that starts after you save,
          whether it's started here or by a deploy. Runs already going keep the
          settings they started with.
        </Banner>
        <div className="mt-6">{body()}</div>
      </div>
    </>
  );
}

export default function McSettingsPage({
  setAuth,
  setUserOrg,
}: McSettingsPageProps) {
  return (
    <McPageShell setAuth={setAuth} setUserOrg={setUserOrg}>
      <McSettingsView />
    </McPageShell>
  );
}
